import { useEffect, useState } from "react";
import { apiCreateGame, apiGameExists, apiJoinGame, apiResume } from "../api";
import { getSavedIdentity, getToken, saveIdentity, saveToken, setLastCode } from "../storage";
import type { GameSession } from "../gameStore";

interface HomeProps {
  initialCode?: string;
  onSession: (s: GameSession) => void;
}

const PRESET_COLORS = ["red", "yellow", "green", "blue", "purple", "pink", "cyan", "orange", "teal"];

export function Home({ initialCode, onSession }: HomeProps): JSX.Element {
  const [mode, setMode] = useState<"choose" | "create" | "join">(
    initialCode ? "join" : "choose",
  );
  const [gameName, setGameName] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [code, setCode] = useState(initialCode ?? "");
  const [name, setName] = useState("");
  const [color, setColor] = useState(PRESET_COLORS[0]!);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = getSavedIdentity();
    if (id) {
      setName(id.name);
      setColor(id.color);
    }
  }, []);

  // Try silent resume of any saved token (RC-03).
  useEffect(() => {
    if (initialCode) {
      const tok = getToken(initialCode);
      if (tok) {
        void apiResume(tok).then((r) => {
          if (r.ok && r.gameCode && r.participantId && r.role) {
            setLastCode(r.gameCode);
            onSession({
              gameCode: r.gameCode,
              participantId: r.participantId,
              role: r.role,
              token: tok,
            });
          }
        });
      }
    }
  }, [initialCode, onSession]);

  const onCreate = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await apiCreateGame({
        gameName: gameName.trim() || undefined,
        password: createPassword || undefined,
      });
      saveToken(r.gameCode, r.token);
      setLastCode(r.gameCode);
      onSession({ ...r });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg === "invalid_password" ? "Wrong password." : msg);
    } finally {
      setBusy(false);
    }
  };

  const onJoin = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (!/^\d{4}$/.test(code)) throw new Error("code must be 4 digits");
      if (!name.trim()) throw new Error("enter your name");
      const exists = await apiGameExists(code);
      if (!exists.exists) throw new Error("game not found");
      const r = await apiJoinGame({ code, name: name.trim(), color });
      saveIdentity({ name: name.trim(), color });
      saveToken(r.gameCode, r.token);
      setLastCode(r.gameCode);
      onSession({
        gameCode: r.gameCode,
        participantId: r.participantId,
        role: "PHC",
        token: r.token,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app">
      <div className="center">
        <div className="brand">UnoPhone</div>
        <div className="muted">Family card game, one shared screen + per-player phones</div>

        {mode === "choose" && (
          <div className="card col">
            <button onClick={() => setMode("create")} disabled={busy} style={{ padding: "1.1rem 1.3rem", fontSize: "1.05rem" }}>
              Set up the table
              <div style={{ fontWeight: 500, fontSize: "0.85rem", opacity: 0.85, marginTop: 4 }}>
                this device shows the cards
              </div>
            </button>
            <button className="ghost" onClick={() => setMode("join")} disabled={busy} style={{ padding: "1.1rem 1.3rem", fontSize: "1.05rem" }}>
              Join as a player
              <div style={{ fontWeight: 500, fontSize: "0.85rem", opacity: 0.7, marginTop: 4 }}>
                this is my phone
              </div>
            </button>
          </div>
        )}

        {mode === "create" && (
          <div className="card col">
            <label>
              <div className="muted">Game name (optional)</div>
              <input
                value={gameName}
                onChange={(e) => setGameName(e.target.value)}
                placeholder={`${new Date().toLocaleDateString("en-US", { weekday: "long" })} game`}
                maxLength={64}
              />
            </label>
            <label>
              <div className="muted">Password (leave blank if not required)</div>
              <input
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                placeholder="••••••"
                autoComplete="off"
                maxLength={256}
              />
            </label>
            <div className="row">
              <button className="ghost" onClick={() => setMode("choose")} disabled={busy}>
                Back
              </button>
              <button className="right" onClick={onCreate} disabled={busy}>
                {busy ? "Creating…" : "Create game"}
              </button>
            </div>
            {error && <div className="danger-text">{error}</div>}
          </div>
        )}

        {mode === "join" && (
          <div className="card col">
            <label>
              <div className="muted">Game code (4 digits)</div>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                inputMode="numeric"
                pattern="\d{4}"
                maxLength={4}
                placeholder="1234"
                style={{
                  fontSize: "2rem",
                  fontWeight: 800,
                  letterSpacing: "0.5rem",
                  textAlign: "center",
                  paddingLeft: "0.5rem",
                  color: "var(--accent)",
                }}
              />
            </label>
            <label>
              <div className="muted">Your name</div>
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={64} />
            </label>
            <label>
              <div className="muted">Your color</div>
              <div className="row">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    style={{
                      background: PALETTE[c],
                      color: "white",
                      width: 44, height: 44, padding: 0, borderRadius: "50%",
                      outline: color === c ? "3px solid var(--accent)" : "none",
                    }}
                    aria-label={c}
                  />
                ))}
              </div>
            </label>
            <div className="row">
              <button className="ghost" onClick={() => setMode("choose")} disabled={busy}>
                Back
              </button>
              <button className="right" onClick={onJoin} disabled={busy}>
                {busy ? "Joining…" : "Join game"}
              </button>
            </div>
            {error && <div className="danger-text">{error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

const PALETTE: Record<string, string> = {
  red: "#ef4444",
  yellow: "#f59e0b",
  green: "#22c55e",
  blue: "#3b82f6",
  purple: "#a855f7",
  pink: "#ec4899",
  cyan: "#06b6d4",
  orange: "#f97316",
  teal: "#14b8a6",
};
